/* eslint-disable eqeqeq */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { Paper, Tooltip } from "@material-ui/core";
import {
  FilteringState,
  GroupingState,
  IntegratedFiltering,
  IntegratedPaging,
  IntegratedSorting,
  PagingState,
  SortingState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  Table,
  TableFilterRow,
  TableHeaderRow,
  TableColumnResizing,
} from "@devexpress/dx-react-grid-material-ui";

// Custom cell component for long strings without spaces
const LongStringCell = ({ value, column, tableRow, tableColumn, ...restProps }) => {
  const maxLength = 30; // Adjust based on your needs
  const isLongString = value && value.length > maxLength;
  
  // Columns that need special handling for long strings
  const longStringColumns = ['label','description','pageRoutePathLabel', 'categoryLabel','notificationMsg', 'notificationMsg', 'thumbnailFileName'];
  
  if (longStringColumns.includes(column.name) && isLongString) {
    const truncatedValue = `${value.substring(0, maxLength)}...`;
    
    return (
      <Table.Cell {...restProps}>
        <Tooltip title={value} placement="top">
          <div style={{
            maxWidth: '200px',
            wordBreak: 'break-all',
            overflowWrap: 'break-word',
            hyphens: 'auto',
            lineHeight: '1.2',
            cursor: 'help'
          }}>
            {truncatedValue}
          </div>
        </Tooltip>
      </Table.Cell>
    );
  }
  
  // For other columns or shorter strings
  return (
    <Table.Cell {...restProps}>
      <div style={{
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
        hyphens: 'auto',
        lineHeight: '1.2'
      }}>
        {value}
      </div>
    </Table.Cell>
  );
};

// Custom header cell component
const HeaderCell = ({ column, ...restProps }) => (
  <TableHeaderRow.Cell {...restProps}>
    <div style={{
      fontWeight: 'bold',
      fontSize: '14px',
      padding: '8px',
      wordBreak: 'break-word',
      overflowWrap: 'break-word',
      lineHeight: '1.2'
    }}>
      {column.title !== "Action" ? column.title : `\u00A0\u00A0${column.title}`}
    </div>
  </TableHeaderRow.Cell>
);

class R314CallToActionsTableComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      columns: [],
      rows: [],
      formatColumns: [],
      currencyColumns: [],
      availableFilterOperations: [
        "equal",
        "notEqual",
        "greaterThan",
        "greaterThanOrEqual",
        "lessThan",
        "lessThanOrEqual",
      ],
      pageSizes: [5, 10, 15, 20],
      defaultSorting: [],
      sortingStateColumnExtensions: [
        { columnName: "action", sortingEnabled: false },
      ],
      tableColumnExtensions: [
        { columnName: "SRNo", width: 80, align: 'center' },
        { columnName: "label", width: 180, wordWrapEnabled: true },
        { columnName: "isAvailableForAllLabel", width: 120, align: 'center' },
        { columnName: "userLabel", width: 150, wordWrapEnabled: true },
        { columnName: "description", width: 250, wordWrapEnabled: true },
        { columnName: "pageRouteLabel", width: 150, wordWrapEnabled: true },
        { columnName: "pageRoutePathLabel", width: 220, wordWrapEnabled: true },
        { columnName: "notificationTypeLabel", width: 160, wordWrapEnabled: true },
        { columnName: "categoryLabel", width: 180, wordWrapEnabled: true },
        { columnName: "notificationMsg", width: 250, wordWrapEnabled: true },
        { columnName: "thumbnailFileName", width: 150, wordWrapEnabled: true },
        { columnName: "action", width: 120, align: 'center' },
      ],
      defaultColumnWidths: [
        { columnName: "SRNo", width: 80 },
        { columnName: "label", width: 180 },
        { columnName: "isAvailableForAllLabel", width: 120 },
        { columnName: "userLabel", width: 150 },
        { columnName: "description", width: 250 },
        { columnName: "pageRouteLabel", width: 150 },
        { columnName: "pageRoutePathLabel", width: 220 },
        { columnName: "notificationTypeLabel", width: 160 },
        { columnName: "categoryLabel", width: 180 },
        { columnName: "notificationMsg", width: 250 },
        { columnName: "thumbnailFileName", width: 150 },
        { columnName: "action", width: 120 },
      ],
      resizingMode: "widget",
      defaultFilters: [],
      filteringStateColumnExtensions: [
        { columnName: "action", filteringEnabled: false },
      ],
    };
  }

  render() {
    const {
      tableColumnExtensions,
      defaultColumnWidths,
      defaultSorting,
      sortingStateColumnExtensions,
      filteringStateColumnExtensions,
      defaultFilters,
      pageSizes,
    } = this.state;

    const rows = this.props.data;
    const columns = this.props.columns;
    const showFilter = this.props.showFilter;

    return (
      <Paper style={{ 
        width: '100%', 
        overflowX: 'auto',
        // Add custom CSS for long strings
        '& .dx-g-bs4-table-cell': {
          wordBreak: 'break-all',
          overflowWrap: 'break-word',
          hyphens: 'auto',
          lineHeight: '1.3',
          padding: '8px 12px',
          verticalAlign: 'top'
        },
        '& .dx-g-bs4-table-header-cell': {
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          lineHeight: '1.2',
          padding: '12px',
          verticalAlign: 'middle'
        }
      }}>
        <Grid rows={rows} columns={columns}>
          <FilteringState
            defaultFilters={defaultFilters}
            columnExtensions={filteringStateColumnExtensions}
          />
          <SortingState
            defaultSorting={defaultSorting}
            columnExtensions={sortingStateColumnExtensions}
          />
          <PagingState defaultCurrentPage={1} defaultPageSize={10} />
          <IntegratedFiltering />
          <IntegratedSorting />
          <IntegratedPaging />
          
          <Table 
            columnExtensions={tableColumnExtensions}
            cellComponent={LongStringCell}
            style={{ 
              minWidth: '1700px',
              tableLayout: 'fixed' // This helps with consistent column widths
            }}
          />
          
          <TableColumnResizing
            columnWidths={defaultColumnWidths}
            resizingMode="widget"
            onColumnWidthsChange={(columnWidths) => {
              // Allow dynamic resizing
              this.setState({ defaultColumnWidths: columnWidths });
            }}
          />
          
          <TableHeaderRow
            showSortingControls={true}
            cellComponent={HeaderCell}
          />
          
          {showFilter ? <TableFilterRow showFilterSelector={true} /> : ""}
          <PagingPanel pageSizes={pageSizes} />
        </Grid>
      </Paper>
    );
  }
}

R314CallToActionsTableComponent.propTypes = {
  showFilter: PropTypes.bool,
  data: PropTypes.array,
  columns: PropTypes.array,
};

R314CallToActionsTableComponent.defaultProps = {
  showFilter: false,
  data: [],
  columns: [],
};

export default R314CallToActionsTableComponent;